import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BookOpen, Edit, Trash2, Share2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Layout } from "@/components/Layout";
import { storiesApi } from "@/api/stories";
import { Story } from "@/types/database";

interface StoryPage {
  page_number: number;
  text: string;
  image_prompt: string;
  illustration_style: string;
  image_url?: string;
}

const StoryView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [story, setStory] = useState<Story | null>(null);
  const [currentSpread, setCurrentSpread] = useState(0);
  const [loadingStory, setLoadingStory] = useState(true);
  const [error, setError] = useState<string>('');
  const [showCover, setShowCover] = useState(true);

  useEffect(() => {
    if (id) {
      loadStory(id);
    }
  }, [id]);

  const loadStory = async (storyId: string) => {
    try {
      setLoadingStory(true);
      const result = await storiesApi.getStory(storyId);
      if (result.success && result.data) {
        setStory(result.data);
      } else {
        setError(result.error || 'Failed to load story');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoadingStory(false);
    }
  };

  const handleDelete = async () => {
    if (!story || !confirm('Are you sure you want to delete this story?')) return;
    
    try {
      const result = await storiesApi.deleteStory(story.id);
      if (result.success) {
        navigate('/stories');
      } else {
        setError(result.error || 'Failed to delete story');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const getGenreEmoji = (genre: string) => {
    const emojis: { [key: string]: string } = {
      adventure: "🗺️",
      fantasy: "🐉",
      educational: "📚",
      bedtime: "🌙",
      friendship: "🤝",
      nature: "🌿"
    };
    return emojis[genre] || "📖";
  };

  const getGenreColor = (genre: string) => {
    const colors: { [key: string]: string } = {
      adventure: "bg-blue-100 text-blue-800",
      fantasy: "bg-purple-100 text-purple-800",
      educational: "bg-green-100 text-green-800",
      bedtime: "bg-indigo-100 text-indigo-800",
      friendship: "bg-pink-100 text-pink-800",
      nature: "bg-emerald-100 text-emerald-800"
    };
    return colors[genre] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <BookOpen className="h-8 w-8 animate-pulse text-purple-500" />
          <span className="text-lg">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    navigate("/auth");
    return null;
  }

  if (loadingStory) {
    return (
      <Layout>
        <div className="py-6 px-6 max-w-4xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-2">
              <BookOpen className="h-8 w-8 animate-pulse text-purple-500" />
              <span className="text-lg">Loading your story...</span>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !story) {
    return (
      <Layout>
        <div className="py-6 px-6 max-w-4xl mx-auto">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Story Not Found</h2>
            <p className="text-gray-600 mb-6">{error || 'The story you\'re looking for doesn\'t exist.'}</p>
            <Button onClick={() => navigate('/stories')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Stories
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const pages = story.content?.pages || [];
  const totalSpreads = pages.length;
  const firstImageUrl = pages.find(p => p.image_url)?.image_url;

  // Alternating spread: even = text left, image right; odd = image left, text right
  const page = pages[currentSpread];
  const isEvenSpread = currentSpread % 2 === 0;

  return (
    <Layout>
      <div className="py-6 px-6 max-w-6xl mx-auto">
        {/* Book Cover Page */}
        {showCover ? (
          <div className="flex flex-col items-center justify-center min-h-[500px] bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg shadow-lg p-12">
            {firstImageUrl && (
              <img src={firstImageUrl} alt="Story cover" className="w-48 h-48 object-cover rounded-lg shadow mb-8" />
            )}
            <h1 className="text-4xl font-bold text-gray-900 mb-4 text-center">{story.title}</h1>
            {story.description && (
              <p className="text-lg text-gray-600 mb-8 text-center max-w-xl">{story.description}</p>
            )}
            <button
              className="mt-6 px-8 py-3 bg-purple-600 text-white rounded-lg text-lg font-semibold shadow hover:bg-purple-700 transition"
              onClick={() => setShowCover(false)}
            >
              Start Reading
            </button>
          </div>
        ) : (
          <>
            {/* Spread */}
            <div className="flex flex-col md:flex-row gap-8 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-6 shadow-lg" style={{ minHeight: 500 }}>
              {/* Left Page */}
              <div className="flex-1 bg-white rounded-lg shadow relative flex flex-col justify-between" style={{ minHeight: 500 }}>
                {isEvenSpread ? (
                  // Text on left
                  <>
                    <div className="p-8 flex-1 flex flex-col justify-center">
                      <div className="text-lg leading-relaxed text-gray-700 max-w-xl mx-auto">{page.text}</div>
                    </div>
                    <span className="absolute bottom-2 left-4 text-xs text-gray-400">{page.page_number}</span>
                  </>
                ) : (
                  // Image on left
                  <>
                    <div className="flex-1 flex items-center justify-center p-4">
                      {page.image_url ? (
                        <img src={page.image_url} alt="Story illustration" className="w-full h-auto max-h-[400px] object-contain rounded-lg" />
                      ) : (
                        <div className="w-full h-64 bg-gradient-to-br from-purple-200 to-pink-200 rounded-lg flex items-center justify-center">
                          <span className="text-gray-500 text-sm">Generating Image...</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              {/* Right Page */}
              <div className="flex-1 bg-white rounded-lg shadow relative flex flex-col justify-between" style={{ minHeight: 500 }}>
                {!isEvenSpread ? (
                  // Text on right (odd spread)
                  <>
                    <div className="p-8 flex-1 flex flex-col justify-center">
                      <div className="text-lg leading-relaxed text-gray-700 max-w-xl mx-auto">{page.text}</div>
                    </div>
                    <span className="absolute bottom-2 right-4 text-xs text-gray-400">{page.page_number}</span>
                  </>
                ) : (
                  // Image on right (even spread)
                  <>
                    <div className="flex-1 flex items-center justify-center p-4">
                      {page.image_url ? (
                        <img src={page.image_url} alt="Story illustration" className="w-full h-auto max-h-[400px] object-contain rounded-lg" />
                      ) : (
                        <div className="w-full h-64 bg-gradient-to-br from-purple-200 to-pink-200 rounded-lg flex items-center justify-center">
                          <span className="text-gray-500 text-sm">Generating Image...</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
            {/* Navigation */}
            <div className="flex items-center justify-between mt-8">
              <Button
                variant="outline"
                onClick={() => setCurrentSpread(Math.max(0, currentSpread - 1))}
                disabled={currentSpread === 0}
              >
                Previous
              </Button>
              <div className="text-sm text-gray-500">
                Page {currentSpread + 1} of {totalSpreads}
              </div>
              <Button
                variant="outline"
                onClick={() => setCurrentSpread(Math.min(totalSpreads - 1, currentSpread + 1))}
                disabled={currentSpread >= totalSpreads - 1}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default StoryView; 